import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Toaster,
  useToastController,
  Toast,
  ToastTitle, mergeClasses
} from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import StepHero from "../components/immobilieContractStep/StepHero";
import { CONTRACT_SECTIONS } from "../components/immobilieContractStep/sections";
import type {
  ContactSummary,
  ContractAnswers,
  ContractSectionDef,
  ContractStepKind,
  RiskSummary,
  TarifierungKey,
  VersichererKey,
  VertragsartKey,
  VorvertragInfo,
} from "../components/immobilieContractStep/types";
import {
  CCK,
  BEDINGUNGEN_ALLGEMEIN_TEXT,
  BEDINGUNGEN_BESONDERE_TEXT,
} from "../components/immobilieContractStep/characteristicKeys";
import { ContactSelector } from "../components/ContactSelector";
import VertragsBasisSection from "../components/immobilieContractStep/VertragsBasisSection";
import VereinbarungSection, {
  type VereinbarungStatus,
} from "../components/immobilieContractStep/VereinbarungSection";
import RisikoAuswahlSection from "../components/immobilieContractStep/RisikoAuswahlSection";
import RisikoinfoSection from "../components/immobilieContractStep/RisikoinfoSection";
import FremdvertragVergleichTable from "../components/immobilieContractStep/FremdvertragVergleichTable";
import type { UploadedFile } from "../components/immobilieContractStep/FileDropzone";
import {
  ZahlweisePicker,
  InkassoartPicker,
  ZahlungsartPicker,
  ZAHLUNGSART_OPTIONS,
  BankverbindungBlock,
  RechnungsstellungPicker,
} from "../components/immobilieContractStep/ZahlungBlocks";
import {
  VersicherungssummeBlock,
  type VsAnteil,
  UvvPicker,
  type UvvState,
  MietverlustBlock,
  type MietverlustState,
  GefahrenTabelle,
  HaftzeitSelect,
  TemporaereAusschlussBlock,
  type TemporaereAusschlussState,
  BeitragBlock,
} from "../components/immobilieContractStep/VersicherungBlocks";
import {
  anyMietverlust,
  mietverlustGefahrIds,
  type GefahrenFormState,
  type GefahrFormInput,
} from "../components/immobilieContractStep/gefahrenConfig";
import {
  PhotovoltaikBlock,
  BedingungenDokumenteBlock,
  type BedingungBlockState,
  VolldeckungPraemienBlock,
  type VolldeckungPraemienState,
  BemerkungenHinweiseBlock,
  type Hinweis,
  SummaryBlock,
} from "../components/immobilieContractStep/AbschlussBlocks";

const DRAFT_LS_PREFIX = "immobilie-contract-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;

const ZAHLWEISE_TO_FREQUENCY: Record<string, number> = {
  jaehrlich: 1,
  halbjaehrlich: 2,
  vierteljaehrlich: 4,
  monatlich: 12,
};

const useStyles = makeStyles({
  page: {
    paddingTop: "0",
    paddingBottom: "24px",
    paddingLeft: "24px",
    paddingRight: "24px",
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    marginBottom: "12px",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: BRAND.ink,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    flexWrap: "wrap",
  },
  titleStack: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    minWidth: 0,
    flexGrow: 1,
  },
  titleChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    alignItems: "center",
  },
  titleChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: BRAND.ink,
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("999px"),
    lineHeight: 1.2,
  },
  titleChipIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.muted,
  },
  contextPanel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    alignItems: "flex-start",
  },
  contextBlock: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "8px",
    minWidth: 0,
    flexWrap: "wrap",
  },
  contextLabel: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.muted,
  },
  contextName: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  contextSub: {
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  contextRisks: {
    flexGrow: 1,
    minWidth: 0,
  },
  contextRiskList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "inline-flex",
    flexWrap: "wrap",
    columnGap: "12px",
    rowGap: "2px",
  },
  contextRiskItem: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "6px",
  },
  contextRiskPlate: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.1rem",
    color: BRAND.ink,
    fontWeight: 700,
  },
  contextRiskId: {
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  formPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  heroBox: { flexShrink: 0 },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "0",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: { marginTop: "12px" },
  loadingState: {
    paddingTop: "60px",
    paddingBottom: "60px",
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "1rem",
  },
  errorState: {
    paddingTop: "60px",
    paddingBottom: "60px",
    textAlign: "center",
    color: "#a1331c",
    fontSize: "1rem",
  },
  sectionTabs: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  sectionTab: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  sectionTabActive: {
    color: BRAND.white,
  },
});

const buildContactSummary = (c: any, fallbackId: number, role?: string): ContactSummary => {
  const buildDisplay = (): string => {
    if (!c) return `Kontakt ${fallbackId}`;
    if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
    const first = c.firstName ?? c.vorname ?? "";
    const last = c.lastname ?? c.lastName ?? "";
    if (last && first) return `${last}, ${first}`;
    if (last) return String(last);
    if (first) return String(first);
    if (c.companyName) return String(c.companyName);
    return `Kontakt ${fallbackId}`;
  };
  return {
    id: c?.id ?? fallbackId,
    displayName: buildDisplay(),
    role,
    street: c?.defaultAddress?.street ?? c?.address?.street,
    postalCode: c?.defaultAddress?.postalCode ?? c?.address?.postalCode,
    city: c?.defaultAddress?.city ?? c?.address?.city,
    country: c?.defaultAddress?.country ?? c?.address?.country,
  };
};

const initialAnswers = (): ContractAnswers => ({
  contact: null,
  vorvertrag: { exists: "" } as VorvertragInfo,
  versicherer: "" as VersichererKey,
  vertragsart: "" as VertragsartKey | "",
  tarifierung: "" as TarifierungKey | "",

  vereinbarungStatus: "vereinbart" as VereinbarungStatus,
  vereinbarungFile: null as UploadedFile | null,

  hauptrisikoId: null as number | null,
  temporaereRisikoIds: [] as number[],

  baujahr: "",

  vs: {
    wert1914: "",
    baupreisindex: "",
    baupreisindexJahr: "",
    wert1980: "",
    wertzuschlag: "",
    wertzuschlagJahr: "",
  },
  vsAnteile: [] as VsAnteil[],

  uvv: { vereinbart: "", variante: "", sonstigesText: "" } as UvvState,

  gefahren: {} as GefahrenFormState,
  mietverlust: {
    haftzeit: "",
    vsArt: "",
    vsBrutto: "",
    temporaer: "",
    temporaerWelche: "",
    ausschluss: "",
  } as MietverlustState,
  temporaereAusschluss: {} as TemporaereAusschlussState,

  vorvertragGefahren: {} as GefahrenFormState,
  vorvertragMvHaftzeit: "",

  photovoltaik: { vorhanden: "", policennr: "" },

  beitrag: { premiumAnnualGross: "", vatRate: "19" },
  zahlweise: "",
  inkassoart: "makler",
  zahlungsart: "",
  iban: "",
  bic: "",
  bankName: "",
  kontoinhaber: "",
  rechnungsstellung: "",
  volldeckung: {
    ab: "",
    praemieDerzeit: "",
    praemieVolldeckung: "",
    praemieDifferenz: "",
  } as VolldeckungPraemienState,

  bedingungenAllgemein: { text: BEDINGUNGEN_ALLGEMEIN_TEXT, file: null } as BedingungBlockState,
  bedingungenBesondere: { text: BEDINGUNGEN_BESONDERE_TEXT, file: null } as BedingungBlockState,

  bemerkung: "",
  besondereHinweise: [] as Hinweis[],
});

export type ImmobilieContractCreateEmbedded = {
  contactId?: number | null;
  riskObjectId?: number | null;
  contractId?: number | null;
  onSuccess?: (createdId: number | null) => void;
};

type ImmobilieContractCreatePageProps = {
  embedded?: ImmobilieContractCreateEmbedded;
};

const ImmobilieContractCreatePage = ({ embedded }: ImmobilieContractCreatePageProps = {}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const isEmbedded = Boolean(embedded);
  const riskObjectId = isEmbedded
    ? (embedded?.riskObjectId != null ? String(embedded.riskObjectId) : undefined)
    : params.riskObjectId;
  const editContractId = isEmbedded
    ? (embedded?.contractId != null ? String(embedded.contractId) : null)
    : searchParams.get("contractId") ?? null;
  const toasterId = "immobilie-contract-toaster";
  const { dispatchToast } = useToastController(toasterId);

  const [answers, setAnswers] = useState<ContractAnswers>(initialAnswers());
  const [hauptrisikoSummary, setHauptrisikoSummary] = useState<RiskSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [currentSubStepIdx, setCurrentSubStepIdx] = useState(0);

  const draftKey = useMemo(
    () => `${DRAFT_LS_PREFIX}${riskObjectId ?? "new"}`,
    [riskObjectId],
  );

  // Preload Hauptrisiko if a riskObjectId is provided via URL — keeps the
  // "create contract from risk detail page" entry point working.
  useEffect(() => {
    if (!riskObjectId) return;
    const id = Number(riskObjectId);
    if (!Number.isFinite(id)) return;
    setAnswers((prev) => {
      if (prev.hauptrisikoId === id) return prev;
      return { ...prev, hauptrisikoId: id };
    });
  }, [riskObjectId]);

  // Optional ?contactId=… vorausgewählter Kontakt aus dem Kontaktkontext.
  // Greift nur wenn (noch) kein Hauptrisiko gewählt ist — sonst übernehmen
  // wir den Kontakt aus dem Risiko (siehe Effekt oben).
  useEffect(() => {
    const c = isEmbedded ? embedded?.contactId : searchParams.get("contactId");
    if (!c) return;
    const id = Number(c);
    if (!Number.isFinite(id) || id <= 0) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(apiUrl(`contact/${id}`));
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const summary = buildContactSummary(data, id, "Eigentümer");
        setAnswers((prev) => (prev.contact ? prev : { ...prev, contact: summary }));
      } catch (e) {
        console.error("Failed to load contact", id, e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, embedded?.contactId, searchParams.get("contactId")]);

  // Fetch the Hauptrisiko summary whenever its id changes.
  useEffect(() => {
    const id = answers.hauptrisikoId as number | null;
    if (!id) {
      setHauptrisikoSummary(null);
      return;
    }
    const run = async () => {
      try {
        const res = await fetch(apiUrl(`risk-objects/${id}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const charsArr: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }> =
          Array.isArray(data.characteristics) ? data.characteristics : [];
        const characteristics: Record<string, string> = {};
        for (const c of charsArr) {
          if (c.nameBezeichnung) characteristics[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
        }
        const directContact = data.contact;
        const contacts: ContactSummary[] = directContact
          ? [buildContactSummary(directContact, directContact.id ?? id, "Eigentümer")]
          : [];
        const summary: RiskSummary = {
          id: data.id,
          name: data.name ?? "",
          street: characteristics.street ?? "",
          postalCode: characteristics.postalCode ?? "",
          city: characteristics.city ?? "",
          country: characteristics.country ?? "Deutschland",
          skizzeSvg: data.skizzeSvg ?? null,
          characteristics,
          eigentuemerArt:
            characteristics.eigentuemerArt ?? characteristics.eigentumerArt ?? characteristics.eigentuemer ?? undefined,
          contacts,
        };
        setHauptrisikoSummary(summary);

        // If contact is empty and the risk has one — adopt it.
        if (directContact && !((answers as any).contact?.id)) {
          setAnswers((prev) => ({
            ...prev,
            contact: prev.contact ?? buildContactSummary(directContact, directContact.id ?? id, "Eigentümer"),
          }));
        }
      } catch (e) {
        console.error(e);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.hauptrisikoId]);

  // Restore draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      setAnswers((prev) => {
        const incoming = (parsed.answers ?? {}) as Record<string, unknown>;
        const merged: Record<string, unknown> = { ...prev, ...incoming };
        const deepKeys = [
          "vs",
          "vorvertrag",
          "uvv",
          "mietverlust",
          "beitrag",
          "photovoltaik",
          "volldeckung",
          "bedingungenAllgemein",
          "bedingungenBesondere",
        ];
        for (const k of deepKeys) {
          if (incoming[k] && typeof incoming[k] === "object") {
            merged[k] = {
              ...((prev as Record<string, unknown>)[k] as object ?? {}),
              ...(incoming[k] as object),
            };
          }
        }
        return merged as ContractAnswers;
      });
      const totalSecs = CONTRACT_SECTIONS.length;
      const restoredSec = Math.min(Math.max(0, parsed.currentSectionIdx ?? 0), totalSecs - 1);
      const maxStep = (CONTRACT_SECTIONS[restoredSec]?.steps.length ?? 1) - 1;
      const restoredStep = Math.min(Math.max(0, parsed.currentSubStepIdx ?? 0), maxStep);
      setCurrentSectionIdx(restoredSec);
      setCurrentSubStepIdx(restoredStep);
      setDraftSavedAt(parsed.savedAt ?? null);
    } catch {
      // ignore
    }
    const sectionParam = searchParams.get("section");
    if (sectionParam) {
      const idx = CONTRACT_SECTIONS.findIndex((s) => s.key === sectionParam);
      if (idx >= 0) {
        setCurrentSectionIdx(idx);
        setCurrentSubStepIdx(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Edit-Mode: fetch contract und prefill (lightweight) ──────────────
  // Wir prefillen die wichtigsten Felder. Komplexe verschachtelte Strukturen
  // (gefahren, vs, mietverlust, …) bleiben Default — der User sieht beim
  // Edit die bekannte Maske und kann gezielt nachjustieren.
  const editPrefillDoneRef = useRef(false);
  useEffect(() => {
    if (!editContractId) return;
    if (editPrefillDoneRef.current) return;
    editPrefillDoneRef.current = true;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contracts/${editContractId}`));
        if (!res.ok) return;
        const contract = await res.json();
        if (aborted) return;
        const charsArr: Array<{
          nameBezeichnung?: string;
          contractCharacteristicValue?: string;
        }> = Array.isArray(contract.characteristics) ? contract.characteristics : [];
        const charByName = new Map<string, string>();
        for (const c of charsArr) {
          if (c.nameBezeichnung) charByName.set(c.nameBezeichnung, c.contractCharacteristicValue ?? "");
        }
        const read = (k: string): string => charByName.get(k) ?? "";
        const readJson = <T,>(k: string, fallback: T): T => {
          const raw = read(k);
          if (!raw) return fallback;
          try {
            return JSON.parse(raw) as T;
          } catch {
            return fallback;
          }
        };
        // Nur nicht-leere Reads in ein Merge-Objekt übernehmen (leere "" dürfen die
        // Defaults nicht überschreiben).
        const nonEmpty = (obj: Record<string, string>): Record<string, string> =>
          Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ""));

        setAnswers((prev) => {
          const next: ContractAnswers = { ...prev };
          if (read(CCK.versicherer)) next.versicherer = read(CCK.versicherer);
          if (read(CCK.vertragsart)) next.vertragsart = read(CCK.vertragsart);
          if (read(CCK.tarifierung)) next.tarifierung = read(CCK.tarifierung);
          if (read(CCK.inkassoart)) next.inkassoart = read(CCK.inkassoart);
          if (read(CCK.rechnungsstellung)) next.rechnungsstellung = read(CCK.rechnungsstellung);
          if (read(CCK.vereinbarungStatus)) next.vereinbarungStatus = read(CCK.vereinbarungStatus);
          const hauptId = Number(read(CCK.hauptrisikoId));
          if (Number.isFinite(hauptId) && hauptId > 0) next.hauptrisikoId = hauptId;
          // Vorvertrag-Bag (alle Sub-Keys lesen).
          const vor: Record<string, string> = {
            exists: read(CCK.vorvertragExists),
            isFremdvertrag: read(CCK.vorvertragIsFremd),
            existingContractId: read(CCK.vorvertragExistingId),
            gesellschaft: read(CCK.vorvertragGesellschaft),
            vertragsnummer: read(CCK.vorvertragNr),
            alreadyCancelled: read(CCK.vorvertragAlreadyCancelled),
            kuendigungZum: read(CCK.vorvertragKuendigung),
            kuendigungsgrund: read(CCK.vorvertragKuendigungsgrund),
            shouldCancel: read(CCK.vorvertragShouldCancel),
          };
          // Nur Felder mit Inhalt mergen, sonst überschreibt "" das Default.
          const vorFiltered = Object.fromEntries(Object.entries(vor).filter(([, v]) => v));
          if (Object.keys(vorFiltered).length) {
            next.vorvertrag = { ...(prev.vorvertrag as object ?? {}), ...vorFiltered };
          }

          // ── Skalare / einfache Strings ────────────────────────────────────
          if (read("immo.baujahr")) next.baujahr = read("immo.baujahr");
          if (contract.remark) next.bemerkung = String(contract.remark);
          if (read("immo.zahlweise")) next.zahlweise = read("immo.zahlweise");
          if (read("immo.zahlungsart")) next.zahlungsart = read("immo.zahlungsart");

          // ── Beitrag (Prämie + USt) aus den Vertrags-Skalaren ──────────────
          if (contract.premiumAnnualGross != null || contract.vatRate != null) {
            next.beitrag = {
              premiumAnnualGross:
                contract.premiumAnnualGross != null
                  ? String(contract.premiumAnnualGross)
                  : (prev.beitrag as { premiumAnnualGross: string }).premiumAnnualGross,
              vatRate:
                contract.vatRate != null
                  ? String(Math.round(Number(contract.vatRate) * 10000) / 100)
                  : (prev.beitrag as { vatRate: string }).vatRate,
            };
          }

          // ── Bankverbindung (JSON) ─────────────────────────────────────────
          const bank = readJson<Partial<{ iban: string; bic: string; bankName: string; kontoinhaber: string }>>(
            "immo.bankverbindung",
            {},
          );
          if (bank.iban) next.iban = bank.iban;
          if (bank.bic) next.bic = bank.bic;
          if (bank.bankName) next.bankName = bank.bankName;
          if (bank.kontoinhaber) next.kontoinhaber = bank.kontoinhaber;

          // ── Versicherungssumme ────────────────────────────────────────────
          const vsMerge = nonEmpty({
            wert1914: read(CCK.vs1914),
            baupreisindex: read(CCK.vsBaupreisindex),
            baupreisindexJahr: read(CCK.vsBaupreisindexJahr),
            wert1980: read(CCK.vs1980),
            wertzuschlag: read(CCK.vsWertzuschlag),
            wertzuschlagJahr: read(CCK.vsWertzuschlagJahr),
          });
          if (Object.keys(vsMerge).length) next.vs = { ...(prev.vs as Record<string, string>), ...vsMerge };
          const vsAnteile = readJson<VsAnteil[]>(CCK.vsAnteileJson, []);
          if (vsAnteile.length) next.vsAnteile = vsAnteile;

          // ── UVV ───────────────────────────────────────────────────────────
          const uvvMerge = nonEmpty({
            vereinbart: read(CCK.uvvVereinbartJa),
            variante: read(CCK.uvvVariante),
            sonstigesText: read(CCK.uvvSonstiges),
          });
          if (Object.keys(uvvMerge).length) next.uvv = { ...(prev.uvv as UvvState), ...uvvMerge } as UvvState;

          // ── Gefahren (Neuvertrag): Auswahl, Beträge und Mietverlust je Gefahr ──
          const gefahrenJson = readJson<GefahrenFormState>(CCK.gefahrenJson, {} as GefahrenFormState);
          if (Object.keys(gefahrenJson).length) {
            next.gefahren = gefahrenJson;
          } else {
            // Verträge aus der Zeit vor der Gefahren-Tabelle: Auswahl, Beträge und
            // Mietverlust lagen in getrennten Merkmalen.
            const g = { ...(prev.gefahren as GefahrenFormState) } as GefahrenFormState;
            const selRaw = read("immo.gefahren_selected");
            for (const id of selRaw.split(",").map((x) => x.trim()).filter(Boolean)) {
              g[id] = { ...(g[id] ?? {}), sollVersichertWerden: "ja" };
            }
            const legacyBetrag = (v: unknown): string => {
              if (v == null) return "";
              const raw = typeof v === "object" ? String((v as { value?: unknown }).value ?? "") : String(v);
              return raw.replace(/[^0-9]/g, "");
            };
            const applyMap = (map: Record<string, unknown>, key: keyof GefahrFormInput) => {
              for (const [id, v] of Object.entries(map)) {
                const betrag = legacyBetrag(v);
                if (betrag) g[id] = { ...(g[id] ?? {}), [key]: betrag };
              }
            };
            applyMap(readJson<Record<string, unknown>>(CCK.hePerGefahrJson, {}), "hoechstentschaedigung");
            applyMap(readJson<Record<string, unknown>>(CCK.jhePerGefahrJson, {}), "jahreshoechstentschaedigung");
            applyMap(readJson<Record<string, unknown>>(CCK.sbPerGefahrJson, {}), "selbstbeteiligung");
            // Galt „alle": jeder gewählten Gefahr denselben Betrag geben.
            if (read(CCK.heScope) === "alle" && legacyBetrag(read(CCK.heValue))) {
              for (const id of Object.keys(g)) g[id].hoechstentschaedigung = legacyBetrag(read(CCK.heValue));
            }
            if (read(CCK.jheScope) === "alle" && legacyBetrag(read(CCK.jheValue))) {
              for (const id of Object.keys(g)) g[id].jahreshoechstentschaedigung = legacyBetrag(read(CCK.jheValue));
            }
            const mvLegacyPer = readJson<Record<string, string>>(CCK.mvHaftzeitPerGefahr, {});
            if (read(CCK.mvScope) === "alle") {
              for (const id of Object.keys(g)) g[id].mietverlust = "ja";
            } else {
              for (const id of Object.keys(mvLegacyPer)) g[id] = { ...(g[id] ?? {}), mietverlust: "ja" };
            }
            next.gefahren = g;
          }

          // ── Mietverlust (Haftzeit + Höhe, gilt für alle Gefahren mit MV) ──
          const mvLegacyHaftzeit = Object.values(
            readJson<Record<string, string>>(CCK.mvHaftzeitPerGefahr, {}),
          )[0];
          const mvMerge: Record<string, unknown> = nonEmpty({
            haftzeit: read(CCK.mvHaftzeit) || mvLegacyHaftzeit || "",
            vsArt: read(CCK.mvVsArt),
            vsBrutto: read(CCK.mvVsBrutto),
            temporaer: read(CCK.mvTemporaer),
            temporaerWelche: read(CCK.mvTemporaerWelche),
            ausschluss: read(CCK.mvAusschluss),
          });
          if (Object.keys(mvMerge).length) {
            next.mietverlust = { ...(prev.mietverlust as MietverlustState), ...mvMerge } as MietverlustState;
          }

          // ── Vorvertrags-Gefahren (nur Fremdvertrag) ───────────────────────
          const vvGefahren = readJson<GefahrenFormState>(CCK.vorvertragGefahrenJson, {} as GefahrenFormState);
          if (Object.keys(vvGefahren).length) next.vorvertragGefahren = vvGefahren;
          if (read(CCK.vorvertragMvHaftzeit)) next.vorvertragMvHaftzeit = read(CCK.vorvertragMvHaftzeit);

          // ── Volldeckung (Differenzdeckung) ───────────────────────────────
          const vdMerge = nonEmpty({
            ab: read(CCK.volldeckungAb),
            praemieDerzeit: read(CCK.praemieDerzeit),
            praemieVolldeckung: read(CCK.praemieVolldeckung),
            praemieDifferenz: read(CCK.praemieDifferenz),
          });
          if (Object.keys(vdMerge).length) next.volldeckung = { ...(prev.volldeckung as VolldeckungPraemienState), ...vdMerge } as VolldeckungPraemienState;

          // ── Photovoltaik ──────────────────────────────────────────────────
          const pvMerge = nonEmpty({ vorhanden: read(CCK.pvVorhanden), policennr: read(CCK.pvPolicennr) });
          if (Object.keys(pvMerge).length) next.photovoltaik = { ...(prev.photovoltaik as { vorhanden: string; policennr: string }), ...pvMerge };

          // ── Bedingungen (nur Texte; Datei-Uploads nicht rekonstruierbar) ──
          if (read(CCK.bedingungenAllgemeinText)) {
            next.bedingungenAllgemein = { ...(prev.bedingungenAllgemein as BedingungBlockState), text: read(CCK.bedingungenAllgemeinText) } as BedingungBlockState;
          }
          if (read(CCK.bedingungenBesondereText)) {
            next.bedingungenBesondere = { ...(prev.bedingungenBesondere as BedingungBlockState), text: read(CCK.bedingungenBesondereText) } as BedingungBlockState;
          }

          // ── Temporäre Risiken + besondere Hinweise ───────────────────────
          const tempIds = readJson<number[]>(CCK.temporaereRisikoIdsJson, []);
          if (tempIds.length) next.temporaereRisikoIds = tempIds;
          const tempAus = readJson<TemporaereAusschlussState>(CCK.temporaereAusschlussJson, {} as TemporaereAusschlussState);
          if (Object.keys(tempAus).length) next.temporaereAusschluss = tempAus;
          const hinweise = readJson<Hinweis[]>(CCK.besondereHinweiseJson, []);
          if (hinweise.length) next.besondereHinweise = hinweise;

          return next;
        });

        if (contract.contactId) {
          try {
            const cRes = await fetch(apiUrl(`contact/${contract.contactId}`));
            if (cRes.ok) {
              const c = await cRes.json();
              if (!aborted) {
                const summary = buildContactSummary(c, contract.contactId, "Versicherungsnehmer");
                setAnswers((prev) => ({ ...prev, contact: summary }));
              }
            }
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error("Edit-Mode Immo contract fetch failed", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [editContractId]);

  // Autosave draft
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            answers,
            currentSectionIdx,
            currentSubStepIdx,
            savedAt,
          }),
        );
        setDraftSavedAt(savedAt);
      } catch {
        // ignore
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [answers, currentSectionIdx, currentSubStepIdx, draftKey]);

  // Compute visible steps. Hidden steps are skipped on Next/Prev.
  const visibleSections = useMemo<ContractSectionDef[]>(() => {
    return CONTRACT_SECTIONS.map((sec) => ({
      ...sec,
      steps: sec.steps.filter((s) => {
        if (!s.showWhen) return true;
        return s.showWhen({
          riskCharacteristics: hauptrisikoSummary?.characteristics ?? {},
          answers,
        });
      }),
    })).filter((sec) => sec.steps.length > 0);
  }, [hauptrisikoSummary, answers]);

  useEffect(() => {
    if (!visibleSections.length) return;
    if (currentSectionIdx >= visibleSections.length) {
      setCurrentSectionIdx(visibleSections.length - 1);
      setCurrentSubStepIdx(0);
      return;
    }
    const sec = visibleSections[currentSectionIdx];
    if (currentSubStepIdx >= sec.steps.length) {
      setCurrentSubStepIdx(sec.steps.length - 1);
    }
  }, [visibleSections, currentSectionIdx, currentSubStepIdx]);

  const totalSteps = useMemo(
    () => visibleSections.reduce((acc, s) => acc + s.steps.length, 0),
    [visibleSections],
  );
  const stepIndex = useMemo(() => {
    let idx = 0;
    for (let s = 0; s < currentSectionIdx; s++) idx += visibleSections[s].steps.length;
    return idx + currentSubStepIdx;
  }, [visibleSections, currentSectionIdx, currentSubStepIdx]);
  const progressFraction = totalSteps > 0 ? stepIndex / Math.max(1, totalSteps - 1) : 0;
  const isLastStep =
    currentSectionIdx === visibleSections.length - 1 &&
    currentSubStepIdx === (visibleSections[currentSectionIdx]?.steps.length ?? 1) - 1;

  const goPrev = () => {
    if (currentSubStepIdx > 0) setCurrentSubStepIdx(currentSubStepIdx - 1);
    else if (currentSectionIdx > 0) {
      const prev = currentSectionIdx - 1;
      setCurrentSectionIdx(prev);
      setCurrentSubStepIdx(visibleSections[prev].steps.length - 1);
    }
  };
  const goNext = () => {
    const sec = visibleSections[currentSectionIdx];
    if (currentSubStepIdx < sec.steps.length - 1) {
      setCurrentSubStepIdx(currentSubStepIdx + 1);
    } else if (currentSectionIdx < visibleSections.length - 1) {
      setCurrentSectionIdx(currentSectionIdx + 1);
      setCurrentSubStepIdx(0);
    }
  };

  const setAnswer = <K extends keyof ContractAnswers>(key: K, value: ContractAnswers[K]) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };
  const updateNested = <T,>(key: keyof ContractAnswers, patch: Partial<T>) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: { ...((prev[key] as unknown) as object), ...patch },
    }));
  };

  const saveDraftNow = () => {
    setSavingDraft(true);
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ answers, currentSectionIdx, currentSubStepIdx, savedAt }),
      );
      setDraftSavedAt(savedAt);
    } catch {
      // ignore
    } finally {
      setTimeout(() => setSavingDraft(false), 250);
    }
  };

  // ── Submission ────────────────────────────────────────
  const buildPayload = () => {
    const a = answers;
    const contact = a.contact as ContactSummary | null;
    const hauptrisikoId = a.hauptrisikoId as number | null;
    const temporaereRisikoIds = (a.temporaereRisikoIds as number[]) ?? [];
    if (!hauptrisikoId) return null;

    const zahlungsartOption = ZAHLUNGSART_OPTIONS.find((o) => o.value === a.zahlungsart);
    const paymentMethodID = zahlungsartOption?.paymentMethodID ?? null;
    const paymentFrequency = ZAHLWEISE_TO_FREQUENCY[String(a.zahlweise)] ?? null;

    const cleanNum = (v: string) => {
      const n = parseFloat(String(v).replace(",", "."));
      return Number.isFinite(n) ? n : null;
    };
    const beitrag = a.beitrag as { premiumAnnualGross: string; vatRate: string };
    const premiumAnnualGross = cleanNum(beitrag.premiumAnnualGross);
    const vatRate = cleanNum(beitrag.vatRate) ?? 0.19;
    const vatRateFraction = vatRate > 1 ? vatRate / 100 : vatRate;

    const characteristics: Array<{ nameBezeichnung: string; contractCharacteristicValue: string }> = [];
    const push = (name: string, value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === "") return;
      characteristics.push({ nameBezeichnung: name, contractCharacteristicValue: String(value) });
    };

    // ── Allgemeine Informationen
    const vor = a.vorvertrag as VorvertragInfo;
    push(CCK.vorvertragExists, vor.exists);
    push(CCK.vorvertragIsFremd, vor.isFremdvertrag);
    push(CCK.vorvertragExistingId, vor.existingContractId);
    push(CCK.vorvertragGesellschaft, vor.gesellschaft);
    push(CCK.vorvertragNr, vor.vertragsnummer);
    push(CCK.vorvertragAlreadyCancelled, vor.alreadyCancelled);
    push(CCK.vorvertragKuendigung, vor.kuendigungZum);
    push(CCK.vorvertragKuendigungsgrund, vor.kuendigungsgrund);
    push(CCK.vorvertragShouldCancel, vor.shouldCancel);
    push(CCK.versicherer, String(a.versicherer));
    push(CCK.vertragsart, String(a.vertragsart));
    push(CCK.tarifierung, String(a.tarifierung));

    push(CCK.inkassoart, String(a.inkassoart));
    push(CCK.rechnungsstellung, String(a.rechnungsstellung));

    // ── Vereinbarung
    push(CCK.vereinbarungStatus, String(a.vereinbarungStatus));
    const vFile = a.vereinbarungFile as UploadedFile | null;
    if (vFile?.name) push(CCK.vereinbarungDocId, vFile.name);

    // ── Risiko
    push(CCK.hauptrisikoId, hauptrisikoId);
    if (temporaereRisikoIds.length) push(CCK.temporaereRisikoIdsJson, JSON.stringify(temporaereRisikoIds));
    const tempAus = a.temporaereAusschluss as TemporaereAusschlussState;
    const tempAusFiltered: Record<number, string> = {};
    for (const id of temporaereRisikoIds) {
      const v = tempAus?.[id];
      if (v) tempAusFiltered[id] = v;
    }
    if (Object.keys(tempAusFiltered).length) {
      push(CCK.temporaereAusschlussJson, JSON.stringify(tempAusFiltered));
    }

    // ── Baujahr (read-only from risk; persisted as user override)
    push("immo.baujahr", String(a.baujahr ?? ""));

    // ── Versicherungssumme
    const vs = a.vs as Record<string, string>;
    push(CCK.vs1914, vs.wert1914);
    push(CCK.vsBaupreisindex, vs.baupreisindex);
    push(CCK.vsBaupreisindexJahr, vs.baupreisindexJahr);
    push(CCK.vs1980, vs.wert1980);
    push(CCK.vsWertzuschlag, vs.wertzuschlag);
    push(CCK.vsWertzuschlagJahr, vs.wertzuschlagJahr);
    if ((a.vsAnteile as VsAnteil[]).length) {
      push(CCK.vsAnteileJson, JSON.stringify(a.vsAnteile));
    }

    // ── UVV
    const uvv = a.uvv as UvvState;
    push(CCK.uvvVereinbartJa, uvv.vereinbart);
    push(CCK.uvvVariante, uvv.variante);
    push(CCK.uvvSonstiges, uvv.sonstigesText);

    // ── Gefahren (Neuvertrag): Auswahl, Beträge und Mietverlust je Gefahr
    const gefahren = (a.gefahren as GefahrenFormState) ?? {};
    const selectedIds = Object.entries(gefahren)
      .filter(([, v]) => v?.sollVersichertWerden === "ja")
      .map(([id]) => id);
    // Nur die tatsächlich versicherten Gefahren wandern in den Vertrag.
    const gefahrenClean: GefahrenFormState = {};
    for (const id of selectedIds) gefahrenClean[id] = gefahren[id];
    if (selectedIds.length) {
      push(CCK.gefahrenJson, JSON.stringify(gefahrenClean));
      push("immo.gefahren_selected", selectedIds.join(","));
    }

    // Beträge zusätzlich als flache Maps — so bleiben sie ohne JSON-Parsing auswertbar.
    const betragMap = (key: "hoechstentschaedigung" | "jahreshoechstentschaedigung" | "selbstbeteiligung") => {
      const out: Record<string, string> = {};
      for (const id of selectedIds) {
        const v = gefahren[id]?.[key];
        if (v) out[id] = v;
      }
      return out;
    };
    const heMap = betragMap("hoechstentschaedigung");
    if (Object.keys(heMap).length) push(CCK.hePerGefahrJson, JSON.stringify(heMap));
    const jheMap = betragMap("jahreshoechstentschaedigung");
    if (Object.keys(jheMap).length) push(CCK.jhePerGefahrJson, JSON.stringify(jheMap));
    const sbMap = betragMap("selbstbeteiligung");
    if (Object.keys(sbMap).length) push(CCK.sbPerGefahrJson, JSON.stringify(sbMap));

    // ── Mietverlust — Gefahren, Haftzeit und Höhe
    const mvIds = selectedIds.filter((id) => gefahren[id]?.mietverlust === "ja");
    const mv = a.mietverlust as MietverlustState;
    if (mvIds.length) {
      push(CCK.mvGefahren, mvIds.join(","));
      push(CCK.mvHaftzeit, mv.haftzeit);
      push(CCK.mvVsArt, mv.vsArt);
      if (mv.vsArt === "brutto") push(CCK.mvVsBrutto, mv.vsBrutto);
    }
    push(CCK.mvTemporaer, mv.temporaer);
    push(CCK.mvTemporaerWelche, mv.temporaerWelche);
    push(CCK.mvAusschluss, mv.ausschluss);

    // ── Vorvertrags-Gefahren (nur Fremdvertrag)
    if (vor.isFremdvertrag === "ja") {
      push(CCK.vorvertragGefahrenJson, JSON.stringify(a.vorvertragGefahren ?? {}));
      push(CCK.vorvertragMvHaftzeit, String(a.vorvertragMvHaftzeit ?? ""));
    }

    // ── Volldeckung (Differenzdeckung) — nur bei Fremdvertrag
    const vd = a.volldeckung as VolldeckungPraemienState;
    if (vor.isFremdvertrag === "ja") {
      push(CCK.volldeckungAb, vd.ab);
      push(CCK.praemieDerzeit, vd.praemieDerzeit);
      push(CCK.praemieVolldeckung, vd.praemieVolldeckung);
      push(CCK.praemieDifferenz, vd.praemieDifferenz);
    }

    // ── Photovoltaik
    const pv = a.photovoltaik as { vorhanden: string; policennr: string };
    push(CCK.pvVorhanden, pv.vorhanden);
    if (pv.vorhanden === "ja") push(CCK.pvPolicennr, pv.policennr);

    // ── Bedingungen & Dokumente
    const bedAllg = a.bedingungenAllgemein as BedingungBlockState;
    const bedBes = a.bedingungenBesondere as BedingungBlockState;
    push(CCK.bedingungenAllgemeinText, bedAllg.text);
    if (bedAllg.file?.name) push(CCK.bedingungenAllgemeinDocId, bedAllg.file.name);
    push(CCK.bedingungenBesondereText, bedBes.text);
    if (bedBes.file?.name) push(CCK.bedingungenBesondereDocId, bedBes.file.name);

    if ((a.besondereHinweise as Hinweis[]).length) {
      push(CCK.besondereHinweiseJson, JSON.stringify(a.besondereHinweise));
    }

    const splitFactor = paymentFrequency || 1;
    const premiumByFrequencyGross =
      premiumAnnualGross != null ? Number((premiumAnnualGross / splitFactor).toFixed(2)) : 0;

    const contractNbr = `IMMO-${Date.now()}`;
    // Nur das Hauptrisiko hart verknüpfen — der Immobilien-Vertrag ist ein
    // Einzelvertrag (isCollectiveContract:false). Die temporären Risiken bleiben
    // als Vertrags-Merkmal (temporaereRisikoIdsJson) erhalten; würden sie zusätzlich
    // in riskObjectIds wandern, wirft das Backend „Einzelverträge dürfen nur ein
    // Risikoobjekt enthalten" (400).
    const riskObjectIds = hauptrisikoId != null ? [hauptrisikoId] : [];
    const ownerId = contact?.id ?? null;

    return {
      contractNbr,
      branch: "Immobilien (Gebäude)",
      contactId: ownerId,
      remark: a.bemerkung ? String(a.bemerkung) : null,
      paymentMethodID,
      paymentFrequency,
      premiumByFrequencyGross,
      premiumAnnualGross,
      vatRate: vatRateFraction,
      currency: "EUR",
      isCollectiveContract: false,
      riskObjectIds,
      mainDueDate: (a.volldeckung as VolldeckungPraemienState).ab || null,
      beginPolicyTerm: vor.kuendigungZum || null,
      characteristics: [
        ...characteristics,
        {
          nameBezeichnung: "immo.bankverbindung",
          contractCharacteristicValue: JSON.stringify({
            iban: a.iban,
            bic: a.bic,
            bankName: a.bankName,
            kontoinhaber: a.kontoinhaber,
          }),
        },
        { nameBezeichnung: "immo.zahlungsart", contractCharacteristicValue: String(a.zahlungsart) },
        { nameBezeichnung: "immo.zahlweise", contractCharacteristicValue: String(a.zahlweise) },
      ],
    };
  };

  const onSubmit = async () => {
    const payload = buildPayload();
    if (!payload) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte wählen Sie zuerst ein Hauptrisiko.</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = Boolean(editContractId);
      const url = isEdit ? apiUrl(`contracts/${editContractId}`) : apiUrl("contracts");
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        let detail = "";
        try {
          const parsed = JSON.parse(text);
          const errors = parsed?.message?.errors ?? parsed?.errors;
          if (errors && typeof errors === "object") {
            detail = Object.values(errors).filter(Boolean).join(" · ");
          }
          if (!detail && typeof parsed?.message === "string") {
            detail = parsed.message;
          }
        } catch {
          if (text) detail = text.slice(0, 200);
        }
        throw new Error(detail || `HTTP ${res.status}`);
      }
      let resultId: number | null = isEdit ? Number(editContractId) : null;
      try {
        const created = await res.json();
        resultId = created?.id ?? resultId;
      } catch {
        /* empty body OK */
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore
      }
      dispatchToast(
        <Toast>
          <ToastTitle>{isEdit ? "Vertrag aktualisiert" : "Vertrag erstellt"}</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      if (isEmbedded) {
        embedded?.onSuccess?.(resultId);
      } else {
        navigate(`/contracts/${resultId ?? ""}`);
      }
    } catch (e) {
      console.error(e);
      const message = e instanceof Error ? e.message : "";
      dispatchToast(
        <Toast>
          <ToastTitle>
            Speichern fehlgeschlagen{message ? ` — ${message}` : ""}
          </ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────
  const section = visibleSections[currentSectionIdx] ?? visibleSections[0];
  const step = section?.steps[currentSubStepIdx] ?? section?.steps[0];
  const accent = section ? sectionColorHex[section.color] : BRAND.purple;

  const subPanelStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    rowGap: 10,
    paddingTop: 16,
    paddingBottom: 16,
    borderTop: "1px dashed #ECE7F0",
  };
  const subTitleStyle: React.CSSProperties = {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  };

  const renderStep = (kind: ContractStepKind) => {
    switch (kind) {
      case "kontaktRisiko": {
        const contact = answers.contact as ContactSummary | null;
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 28 }}>
            <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
              <ContactSelector
                label="Versicherungsnehmer / Eigentümer"
                placeholder="Kontakt suchen (Name, Firma)…"
                selectedContact={contact}
                onSelect={(c) => {
                  if (!c) return;
                  const summary = buildContactSummary(c, c.id ?? 0, "Eigentümer");
                  setAnswers((p) => {
                    const sameContact = (p.contact as ContactSummary | null)?.id === summary.id;
                    if (sameContact) return { ...p, contact: summary };
                    return {
                      ...p,
                      contact: summary,
                      hauptrisikoId: null,
                      temporaereRisikoIds: [],
                    };
                  });
                }}
              />
              {contact && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: 4,
                    padding: "12px 14px",
                    backgroundColor: BRAND.bg,
                    borderRadius: 8,
                    border: `1px solid ${BRAND.borderDashed}`,
                  }}
                >
                  <span className={styles.contextLabel}>Ausgewählter Eigentümer</span>
                  <span className={styles.contextName}>{contact.displayName}</span>
                  <span className={styles.contextSub}>
                    {[contact.street, contact.postalCode, contact.city].filter(Boolean).join(" · ")}
                  </span>
                </div>
              )}
            </div>
            <RisikoAuswahlSection
              contactId={(answers.contact as ContactSummary | null)?.id ?? null}
              hauptrisikoId={answers.hauptrisikoId as number | null}
              temporaereRisikoIds={(answers.temporaereRisikoIds as number[]) ?? []}
              onChangeHaupt={(id) => setAnswer("hauptrisikoId", id as unknown as ContractAnswers["hauptrisikoId"])}
              onChangeTemporaere={(ids) =>
                setAnswer("temporaereRisikoIds", ids as unknown as ContractAnswers["temporaereRisikoIds"])
              }
            />
          </div>
        );
      }
      case "vertragsBasis":
        return (
          <VertragsBasisSection
            vorvertrag={answers.vorvertrag as VorvertragInfo}
            versicherer={answers.versicherer as VersichererKey}
            vertragsart={answers.vertragsart as VertragsartKey | ""}
            tarifierung={answers.tarifierung as TarifierungKey | ""}
            onVorvertragChange={(v) => setAnswer("vorvertrag", v as unknown as ContractAnswers["vorvertrag"])}
            onVersichererChange={(v) => setAnswer("versicherer", v as unknown as ContractAnswers["versicherer"])}
            onVertragsartChange={(v) => setAnswer("vertragsart", v as unknown as ContractAnswers["vertragsart"])}
            onTarifierungChange={(v) => setAnswer("tarifierung", v as unknown as ContractAnswers["tarifierung"])}
          />
        );
      case "vereinbarung":
        return (
          <VereinbarungSection
            versicherer={answers.versicherer as VersichererKey}
            status={answers.vereinbarungStatus as VereinbarungStatus}
            file={answers.vereinbarungFile as UploadedFile | null}
            onFileChange={(f) => setAnswer("vereinbarungFile", f as unknown as ContractAnswers["vereinbarungFile"])}
          />
        );
      case "risikoInfo":
        return (
          <RisikoinfoSection
            hauptrisiko={hauptrisikoSummary}
            temporaereRisikoIds={(answers.temporaereRisikoIds as number[]) ?? []}
            baujahr={String(answers.baujahr ?? "")}
            onBaujahrChange={(v) => setAnswer("baujahr", v)}
          />
        );
      case "versicherungssumme": {
        const vs = answers.vs as Record<string, string>;
        return (
          <VersicherungssummeBlock
            wert1914={vs.wert1914 ?? ""}
            baupreisindex={vs.baupreisindex ?? ""}
            baupreisindexJahr={vs.baupreisindexJahr ?? ""}
            wert1980={vs.wert1980 ?? ""}
            wertzuschlag={vs.wertzuschlag ?? ""}
            wertzuschlagJahr={vs.wertzuschlagJahr ?? ""}
            anteile={(answers.vsAnteile as VsAnteil[]) ?? []}
            onChange={(k, v) => updateNested<Record<string, string>>("vs", { [k]: v })}
            onChangeAnteile={(next) => setAnswer("vsAnteile", next as unknown as ContractAnswers["vsAnteile"])}
          />
        );
      }
      case "uvv":
        return (
          <UvvPicker
            state={answers.uvv as UvvState}
            onPatch={(patch) => updateNested<UvvState>("uvv", patch)}
          />
        );
      case "gefahren": {
        const gefahren = (answers.gefahren as GefahrenFormState) ?? {};
        const vorGefahren = (answers.vorvertragGefahren as GefahrenFormState) ?? {};
        const mv = answers.mietverlust as MietverlustState;
        const tempIds = (answers.temporaereRisikoIds as number[]) ?? [];
        const tempAus = answers.temporaereAusschluss as TemporaereAusschlussState;
        const isFremd = (answers.vorvertrag as VorvertragInfo).isFremdvertrag === "ja";

        const patchGefahr = (
          feld: "gefahren" | "vorvertragGefahren",
          current: GefahrenFormState,
          rowId: string,
          patch: Partial<GefahrFormInput>,
        ) =>
          setAnswer(feld, {
            ...current,
            [rowId]: { ...(current[rowId] ?? {}), ...patch },
          } as unknown as ContractAnswers["gefahren"]);

        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
            <div style={{ ...subPanelStyle, borderTop: "none", paddingTop: 0 }}>
              <GefahrenTabelle
                state={gefahren}
                onChange={(rowId, patch) => patchGefahr("gefahren", gefahren, rowId, patch)}
                vorvertragState={isFremd ? vorGefahren : undefined}
                onChangeVorvertrag={
                  isFremd
                    ? (rowId, patch) => patchGefahr("vorvertragGefahren", vorGefahren, rowId, patch)
                    : undefined
                }
              />
            </div>

            {anyMietverlust(gefahren) && (
              <div style={subPanelStyle}>
                <h3 style={subTitleStyle}>Mietverlust</h3>
                <MietverlustBlock
                  state={mv}
                  gefahren={gefahren}
                  onPatch={(patch) => updateNested<MietverlustState>("mietverlust", patch)}
                />
                {isFremd && (
                  <div style={{ display: "flex", flexDirection: "column", rowGap: 4, maxWidth: 280, marginTop: 12 }}>
                    <label
                      style={{
                        fontFamily: BRAND.fontPrimary,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: BRAND.muted,
                      }}
                    >
                      Mietverlust-Haftzeit Vorvertrag
                    </label>
                    <HaftzeitSelect
                      value={String(answers.vorvertragMvHaftzeit ?? "")}
                      onChange={(v) =>
                        setAnswer(
                          "vorvertragMvHaftzeit",
                          v as unknown as ContractAnswers["vorvertragMvHaftzeit"],
                        )
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {tempIds.length > 0 && (
              <div style={subPanelStyle}>
                <TemporaereAusschlussBlock
                  risks={tempIds.map((id) => ({ id, label: `Risiko ${id}` }))}
                  state={tempAus}
                  onPatch={(next) =>
                    setAnswer("temporaereAusschluss", next as unknown as ContractAnswers["temporaereAusschluss"])
                  }
                />
              </div>
            )}
          </div>
        );
      }
      case "gefahrenVergleich": {
        const vor = answers.vorvertrag as VorvertragInfo;
        return (
          <FremdvertragVergleichTable
            kuendigungZum={vor.kuendigungZum}
            vorvertragGefahren={(answers.vorvertragGefahren as GefahrenFormState) ?? {}}
            vorvertragMvHaftzeit={String(answers.vorvertragMvHaftzeit ?? "")}
            neuGefahren={(answers.gefahren as GefahrenFormState) ?? {}}
            neuMvHaftzeit={(answers.mietverlust as MietverlustState).haftzeit}
          />
        );
      }
      case "photovoltaik": {
        const pv = answers.photovoltaik as { vorhanden: string; policennr: string };
        return (
          <PhotovoltaikBlock
            vorhanden={pv.vorhanden}
            policennr={pv.policennr}
            onChange={(k, v) =>
              updateNested<{ vorhanden: string; policennr: string }>("photovoltaik", { [k]: v } as Partial<{ vorhanden: string; policennr: string }>)
            }
          />
        );
      }
      case "vertragsdatenZahlung": {
        const b = answers.beitrag as { premiumAnnualGross: string; vatRate: string };
        const isFremd = (answers.vorvertrag as VorvertragInfo).isFremdvertrag === "ja";
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 8 }}>
            <div style={{ ...subPanelStyle, borderTop: "none", paddingTop: 0 }}>
              <h3 style={subTitleStyle}>Beitrag Gesamt jährlich</h3>
              <BeitragBlock
                premiumAnnualGross={b.premiumAnnualGross}
                vatRate={b.vatRate}
                zahlweise={String(answers.zahlweise ?? "")}
                onChange={(k, v) =>
                  updateNested<{ premiumAnnualGross: string; vatRate: string }>("beitrag", { [k]: v } as Partial<{ premiumAnnualGross: string; vatRate: string }>)
                }
              />
            </div>
            {isFremd && (
              <div style={subPanelStyle}>
                <h3 style={subTitleStyle}>Volldeckung & Prämienberechnung</h3>
                <VolldeckungPraemienBlock
                  state={answers.volldeckung as VolldeckungPraemienState}
                  onPatch={(patch) => updateNested<VolldeckungPraemienState>("volldeckung", patch)}
                />
              </div>
            )}
            <div style={subPanelStyle}>
              <h3 style={subTitleStyle}>Zahlweise</h3>
              <ZahlweisePicker
                value={String(answers.zahlweise ?? "")}
                zahlungsart={String(answers.zahlungsart ?? "")}
                onChange={(v) => setAnswer("zahlweise", v)}
              />
            </div>
            <div style={subPanelStyle}>
              <h3 style={subTitleStyle}>Inkassoart</h3>
              <InkassoartPicker
                value={String(answers.inkassoart ?? "makler")}
                onChange={(v) => setAnswer("inkassoart", v)}
              />
            </div>
            <div style={subPanelStyle}>
              <h3 style={subTitleStyle}>Zahlungsart</h3>
              <ZahlungsartPicker
                value={String(answers.zahlungsart ?? "")}
                onChange={(v) => setAnswer("zahlungsart", v)}
              />
            </div>
            <div style={subPanelStyle}>
              <h3 style={subTitleStyle}>Bankverbindung</h3>
              <BankverbindungBlock
                iban={String(answers.iban ?? "")}
                bic={String(answers.bic ?? "")}
                bankName={String(answers.bankName ?? "")}
                kontoinhaber={String(answers.kontoinhaber ?? "")}
                onChange={(k, v) => setAnswer(k, v)}
              />
            </div>
            {(answers.zahlungsart === "ueberweisung_courte" || answers.zahlungsart === "abbuchung_courte") && (
              <div style={subPanelStyle}>
                <h3 style={subTitleStyle}>Rechnungsstellung</h3>
                <RechnungsstellungPicker
                  value={String(answers.rechnungsstellung ?? "")}
                  onChange={(v) => setAnswer("rechnungsstellung", v)}
                />
              </div>
            )}
          </div>
        );
      }
      case "bedingungenDokumente":
        return (
          <BedingungenDokumenteBlock
            allgemein={answers.bedingungenAllgemein as BedingungBlockState}
            besondere={answers.bedingungenBesondere as BedingungBlockState}
            onChangeAllgemein={(next) =>
              setAnswer("bedingungenAllgemein", next as unknown as ContractAnswers["bedingungenAllgemein"])
            }
            onChangeBesondere={(next) =>
              setAnswer("bedingungenBesondere", next as unknown as ContractAnswers["bedingungenBesondere"])
            }
          />
        );
      case "bemerkungenHinweise": {
        const mv = answers.mietverlust as MietverlustState;
        return (
          <BemerkungenHinweiseBlock
            bemerkung={String(answers.bemerkung ?? "")}
            onChangeBemerkung={(v) => setAnswer("bemerkung", v)}
            hinweise={(answers.besondereHinweise as Hinweis[]) ?? []}
            onChangeHinweise={(next) => setAnswer("besondereHinweise", next as unknown as ContractAnswers["besondereHinweise"])}
            mietverlustKomplettausschluss={mv.ausschluss === "komplett"}
          />
        );
      }
      case "summary":
        return <SummaryBlock rows={buildSummaryRows(answers, hauptrisikoSummary)} />;
      default:
        return null;
    }
  };

  // ── Loading / error fast-paths ────────────────────────────────────────
  if (!section || !step) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Vertrag anlegen — Immobilie</h1>
          <Button appearance="subtle" onClick={() => navigate(-1)}>Zurück</Button>
        </div>
        <div className={styles.formPanel}>
          <div className={styles.errorState}>Kein sichtbarer Schritt — bitte Auswahl prüfen.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} />
      <div className={styles.stickyHeader}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={() => navigate(-1)}
          >
            Zurück
          </Button>
          <h1 className={styles.pageTitle}>
            {editContractId ? "Vertrag bearbeiten" : "Vertrag anlegen"}
            <span> · Immobilien-Versicherung</span>
            {Boolean(answers.vertragsart) && (
              <span>
                {" · "}
                {(answers.vertragsart as VertragsartKey) === "einzel"
                  ? "Einzelvertrag"
                  : "Bündelvertrag"}
              </span>
            )}
            {Boolean(answers.versicherer) && <span> · Generali</span>}
          </h1>
        </div>
        {editContractId && (
          <div
            style={{
              fontSize: "0.82rem",
              color: BRAND.muted,
              backgroundColor: "rgba(247,168,35,0.15)",
              border: `1px solid ${BRAND.gold}`,
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            Bearbeiten-Modus: Vertrag #{editContractId}. Änderungen werden beim Speichern übernommen.
          </div>
        )}
        {(Boolean(answers.contact) || Boolean(hauptrisikoSummary)) && (() => {
          const contractContact = answers.contact as ContactSummary | null;
          const eigentuemer = hauptrisikoSummary?.contacts?.[0];
          const showEigentuemer =
            eigentuemer && contractContact && eigentuemer.id !== contractContact.id;
          return (
            <div className={styles.contextPanel}>
              {contractContact && (
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Kontakt:</span>
                  <span className={styles.contextName}>
                    {contractContact.displayName}
                  </span>
                </div>
              )}
              {showEigentuemer && (
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Eigentümer:</span>
                  <span className={styles.contextName}>
                    {eigentuemer.displayName}
                  </span>
                </div>
              )}
              {hauptrisikoSummary && (
                <div className={mergeClasses(styles.contextBlock, styles.contextRisks)}>
                  <span className={styles.contextLabel}>Risikoobjekt:</span>
                  <span className={styles.contextName}>
                    {hauptrisikoSummary.name || `${hauptrisikoSummary.street}, ${hauptrisikoSummary.city}`}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        <div className={styles.sectionTabs} role="tablist" aria-label="Abschnitte">
          {visibleSections.map((s, sIdx) => {
            const active = sIdx === currentSectionIdx;
            const color = sectionColorHex[s.color];
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={mergeClasses(styles.sectionTab, active && styles.sectionTabActive)}
                style={active ? { backgroundColor: color, borderColor: color } : { color }}
                onClick={() => {
                  setCurrentSectionIdx(sIdx);
                  setCurrentSubStepIdx(0);
                }}
              >
                {sIdx + 1}. {s.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.formPanel}>
        <span className={styles.sectionPill} style={{ backgroundColor: accent }}>
          {section.title}
        </span>
        <div className={styles.titleRow}>
          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 200 }}>
            <h2 className={styles.title}>{step.title}</h2>
            {step.description && <p className={styles.description}>{step.description}</p>}
          </div>
          <div className={styles.heroBox}>
            <StepHero kind={step.kind} />
          </div>
        </div>
        <div className={styles.questions}>{renderStep(step.kind)}</div>
        <div style={{ marginTop: 20 }}>
          <BottomBar
            onPrev={goPrev}
            onNext={goNext}
            onSubmit={onSubmit}
            onSaveDraft={saveDraftNow}
            canPrev={!(currentSectionIdx === 0 && currentSubStepIdx === 0)}
            isLastStep={isLastStep}
            submitting={submitting}
            savingDraft={savingDraft}
            draftSavedAt={draftSavedAt}
            progressLabel={`Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`}
            progressFraction={progressFraction}
            submitLabel="Vertrag anlegen"
            submittingLabel="Wird angelegt…"
          />
        </div>
      </div>
    </div>
  );
};

const buildSummaryRows = (
  a: ContractAnswers,
  risk: RiskSummary | null,
): Array<{ key: string; value: string }> => {
  const rows: Array<{ key: string; value: string }> = [];
  const contact = a.contact as ContactSummary | null;
  rows.push({ key: "Kontakt / Eigentümer", value: contact?.displayName ?? "—" });
  rows.push({
    key: "Hauptrisikoobjekt",
    value: risk ? risk.name || `${risk.street}, ${risk.city}` : "—",
  });
  const tempIds = (a.temporaereRisikoIds as number[]) ?? [];
  if (tempIds.length) {
    rows.push({ key: "Temporäre Risikoobjekte", value: tempIds.join(", ") });
  }
  const vor = a.vorvertrag as VorvertragInfo;
  rows.push({
    key: "Vorvertrag",
    value:
      vor.exists === "ja"
        ? vor.isFremdvertrag === "ja"
          ? `Fremdvertrag (${vor.gesellschaft || "—"})`
          : "Eigener Vorvertrag"
        : "Kein Vorvertrag",
  });
  rows.push({ key: "Versicherer", value: String(a.versicherer || "—") });
  rows.push({ key: "Vertragsart", value: String(a.vertragsart || "—") });
  rows.push({ key: "Tarifierung", value: String(a.tarifierung || "—") });

  const gefahren = (a.gefahren as GefahrenFormState) ?? {};
  const selected = Object.entries(gefahren).filter(([, v]) => v?.sollVersichertWerden === "ja").length;
  rows.push({ key: "Anzahl versicherter Gefahren", value: String(selected) });

  const mvIds = mietverlustGefahrIds(gefahren);
  const mvState = a.mietverlust as MietverlustState;
  rows.push({
    key: "Mietverlust",
    value: mvIds.length
      ? `${mvIds.length} Gefahr${mvIds.length === 1 ? "" : "en"}, Haftzeit ${
          mvState.haftzeit ? `${mvState.haftzeit} Monate` : "—"
        }, ${
          mvState.vsArt === "20pct"
            ? "20 % der VS pro Versicherungsjahr"
            : mvState.vsArt === "brutto"
              ? "Bruttojahresmietwert pro Versicherungsjahr"
              : "Höhe offen"
        }`
      : "Nicht vereinbart",
  });

  const uvv = a.uvv as UvvState;
  rows.push({
    key: "Unterversicherungsverzicht",
    value: uvv.vereinbart === "ja" ? `Vereinbart (${uvv.variante || "—"})` : uvv.vereinbart === "nein" ? "Nicht vereinbart" : "—",
  });

  const zahlweiseLabel: Record<string, string> = {
    jaehrlich: "Jährlich",
    halbjaehrlich: "Halbjährlich",
    vierteljaehrlich: "Vierteljährlich",
    monatlich: "Monatlich",
  };
  rows.push({ key: "Zahlweise", value: zahlweiseLabel[String(a.zahlweise)] ?? "—" });
  const beitrag = a.beitrag as { premiumAnnualGross: string; vatRate: string };
  rows.push({ key: "Beitrag jährlich (€)", value: beitrag.premiumAnnualGross });

  return rows;
};

export default ImmobilieContractCreatePage;
